import { useCallback, useState } from 'react';
import { useForm, type DefaultValues, type FieldValues, type Path, type UseFormReturn } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import type { ZodType, ZodTypeDef } from 'zod';
import type { ApiError } from '@shared/api/errors';
import { toApiError } from '@shared/api/errors';

/**
 * Wraps `useForm` so backend validation errors land back on the right
 * field automatically. Pattern:
 *
 *   const form = useApiForm({ schema: LoginSchema, defaultValues: { ... } });
 *   const onSubmit = form.handleSubmit(async (values) => {
 *     await form.run(() => loginApi(values));
 *   });
 *
 * Errors with `fieldErrors` populate `form.setError`. Top-level message
 * is exposed as `form.serverMessage` for the form-level banner. Loading
 * state is `form.isSubmitting` (RHF native) plus `form.isPending` (our
 * own — distinct so handlers can run beyond the immediate await).
 */

interface UseApiFormOptions<TSchema extends FieldValues> {
  schema: ZodType<TSchema, ZodTypeDef, unknown>;
  defaultValues?: DefaultValues<TSchema>;
  /** Validate on blur for forms with longer flows; default `onSubmit`. */
  mode?: 'onChange' | 'onBlur' | 'onSubmit' | 'onTouched' | 'all';
}

export interface ApiForm<TSchema extends FieldValues> extends UseFormReturn<TSchema> {
  serverMessage: string | null;
  setServerMessage: (msg: string | null) => void;
  isPending: boolean;
  /** Run an async action; route validation errors back into the form. */
  run: <T>(fn: () => Promise<T>) => Promise<T | undefined>;
}

export function useApiForm<TSchema extends FieldValues>(
  options: UseApiFormOptions<TSchema>,
): ApiForm<TSchema> {
  const form = useForm<TSchema>({
    resolver: zodResolver(options.schema),
    defaultValues: options.defaultValues,
    mode: options.mode ?? 'onSubmit',
  });

  const [serverMessage, setServerMessage] = useState<string | null>(null);
  const [isPending, setPending] = useState(false);

  const run = useCallback(async <T,>(fn: () => Promise<T>): Promise<T | undefined> => {
    setServerMessage(null);
    setPending(true);
    try {
      return await fn();
    } catch (error) {
      const api: ApiError = toApiError(error);
      if (api.fieldErrors) {
        for (const [field, message] of Object.entries(api.fieldErrors)) {
          form.setError(field as Path<TSchema>, { type: 'server', message });
        }
      }
      setServerMessage(api.message);
      return undefined;
    } finally {
      setPending(false);
    }
  }, [form]);

  return {
    ...form,
    serverMessage,
    setServerMessage,
    isPending,
    run,
  };
}
